import { useEffect, useState, useMemo } from "react";
import {
    Activity, HeartPulse,
    Wifi, WifiOff, ChevronLeft, ChevronRight,
    Cctv, AlertCircle, Cpu, X, History, TrendingUp,
    Info
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label,
    XAxis, YAxis, CartesianGrid,
    AreaChart, Area, ComposedChart
} from "recharts";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import { showError } from "../../utils/toast";
import { useTheme } from "../../context/ThemeContext";
import SingleSelectLocalSearchDropdown from "../../components/SingleSelectLocalSearchDropdown";

// --- Interfaces based on the JSON payload ---

interface OverviewStats {
    total: number;
    online: number;
    offline: number;
    auth_failed: number;
    healthy_cameras: number;
    unhealthy_but_reachable: number;
    health_percentage: number;
    last_checked_at: string;
    not_configured: number;

    // Fallbacks for older payload versions
    total_cameras?: number;
    online_cameras?: number;
    offline_cameras?: number;
    unreachable_count?: number;
    auth_failed_count?: number;
    online_percentage?: number;
}

interface CameraUptime {
    asset_id: number;
    camera_name: string;
    ip_address: string;
    asset_status: string;
    downtime_30d_minutes: number;
    uptime_30d_percentage: number;
}

interface ReliabilityCamera {
    asset_id: number;
    camera_name: string;
    ip_address: string;
    client_id: number;
    asset_status: string;
    location: string | null;
    total_offline_events: number;
    total_downtime_minutes: number;
    total_downtime_hours: number;
    last_offline_start: string | null;
    avg_recovery_minutes: number;
    currently_offline_since: string | null;
}

interface ReliabilityDashboard {
    stats: {
        fleet_avg_recovery_minutes: number;
        cameras_never_recovered: number;
        total_cameras_tracked: number;
    };
    cameras: ReliabilityCamera[];
}

interface FlappingCamera {
    asset_id: number;
    camera_name: string;
    ip_address: string;
    client_id: number;
    asset_status: string;
    failure_reason: string | null;
    offline_event_count: number;
    total_downtime_minutes: number;
    longest_outage_minutes: number;
    last_offline_at: string | null;
    currently_open_count: number;
    flap_count?: number;
    last_flap?: string | null;
}

interface Alert {
    id: number;
    asset_id: number | null;
    camera_name: string | null;
    ip_address: string | null;
    event_type: string;
    triggered_at: string;
    event_status: string;
    severity: string;
    message: string | null;
    description: string | null;
    asset_hostname: string | null;
    created: string;
    updated: string;
    event_data: string | null;
}

interface HealthTimeseries {
    hour: string;
    total_cameras: number;
    cameras_online: number;
    cameras_offline: number;
    online_percentage: number;
}

interface DailyTrend {
    date: string;
    downtime_minutes: number;
    uptime_pct: number;
}

interface CameraTrend {
    asset_id: number;
    camera_name: string;
    trend: DailyTrend[];
}

interface DashboardData {
    overview: OverviewStats;
    uptime: {
        stats: {
            total_cameras: number;
            avg_uptime_30d: number;
        };
        cameras: CameraUptime[];
    };
    fleet: any[];
    flapping: FlappingCamera[];
    reliability_dashboard?: ReliabilityDashboard;
    manufacturer_health: any[];
    duration_distribution: any[];
    health_timeseries: HealthTimeseries[];
    camera_trend_30d: CameraTrend[];
    trend_analysis?: {
        daily_events_30d: any[];
        camera_downtime_trend: CameraTrend[];
    };
    alerts: {
        stats: {
            total_open: number;
            camera_offline_open: number;
            auth_failure_open: number;
            total_closed_30d: number;
        };
        open_alerts: Alert[];
        closed_30d: Alert[];
    };
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded-lg shadow-xl z-50">
                <p className="text-gray-900 dark:text-gray-100 text-[10px] font-bold mb-1 uppercase tracking-wider">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-xs mb-0.5">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry?.color || entry?.payload?.fill }} />
                        <span className="text-gray-600 dark:text-gray-300">{entry?.name || entry?.dataKey}:</span>
                        <span className="text-gray-900 dark:text-white font-mono font-medium">{entry?.value}</span>
                        {entry?.payload?.displayPercent && (
                            <span className="text-gray-500 dark:text-gray-400 font-mono text-[10px]">({entry?.payload?.displayPercent}%)</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const TrendTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const d = payload[0].payload;
        return (
            <div className="bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 p-3 rounded-xl shadow-2xl z-50 backdrop-blur-md min-w-[140px]">
                <p className="text-gray-900 dark:text-gray-200 text-[9px] font-black mb-2 uppercase tracking-[0.2em]">{label}</p>
                {d.events !== undefined && (
                    <div className="flex justify-between items-center gap-4">
                        <span className="text-[10px] text-rose-500 dark:text-rose-400 font-bold uppercase">Incident Events</span>
                        <span className="text-[11px] text-gray-900 dark:text-white font-black">{d.events}</span>
                    </div>
                )}
                {d.affected !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-gray-100 dark:border-gray-700/50 pt-1.5">
                        <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold uppercase">Camera Impact</span>
                        <span className="text-[11px] text-indigo-700 dark:text-indigo-200 font-black">{d.affected}</span>
                    </div>
                )}
                {d.still !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-gray-100 dark:border-gray-700/50 pt-1.5">
                        <span className="text-[10px] text-gray-600 dark:text-gray-300 font-bold uppercase">Still Offline</span>
                        <span className="text-[11px] text-gray-900 dark:text-gray-100 font-black">{d.still}</span>
                    </div>
                )}
                {d.percentage !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-blue-500/30 pt-1.5 mt-1">
                        <span className="text-[10px] text-blue-600 dark:text-blue-400 font-black uppercase">Percentage</span>
                        <span className="text-[11px] text-blue-700 dark:text-blue-100 font-black">{d.percentage}%</span>
                    </div>
                )}
                {d.total !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-gray-100 dark:border-gray-700/50 pt-1.5">
                        <span className="text-[10px] text-gray-600 dark:text-gray-300 font-bold uppercase">Total Cameras</span>
                        <span className="text-[11px] text-gray-900 dark:text-white font-black">{d.total}</span>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const HealthTrendTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const d = payload[0].payload;
        return (
            <div className="bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 p-3 rounded-xl shadow-2xl z-50 backdrop-blur-md min-w-[140px]">
                <p className="text-gray-900 dark:text-gray-200 text-[9px] font-black mb-2 uppercase tracking-[0.2em]">{label}</p>
                {d.total !== undefined && (
                    <div className="flex justify-between items-center gap-4">
                        <span className="text-[10px] text-gray-600 dark:text-gray-300 font-bold uppercase">Total Cameras</span>
                        <span className="text-[11px] text-gray-900 dark:text-white font-black">{d.total}</span>
                    </div>
                )}
                {d.online !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-gray-100 dark:border-gray-700/50 pt-1.5">
                        <span className="text-[10px] text-amber-600 dark:text-amber-400 font-bold uppercase">Cameras Online</span>
                        <span className="text-[11px] text-amber-700 dark:text-amber-200 font-black">{d.online}</span>
                    </div>
                )}
                {d.offline !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-gray-100 dark:border-gray-700/50 pt-1.5">
                        <span className="text-[10px] text-rose-600 dark:text-rose-400 font-bold uppercase">Cameras Offline</span>
                        <span className="text-[11px] text-rose-600 dark:text-rose-400 font-black">{d.offline}</span>
                    </div>
                )}
                {d.percentage !== undefined && (
                    <div className="flex justify-between items-center gap-4 border-t border-blue-500/30 pt-1.5 mt-1">
                        <span className="text-[10px] text-blue-600 dark:text-blue-400 font-black uppercase">Percentage</span>
                        <span className="text-[11px] text-blue-700 dark:text-blue-100 font-black">{d.percentage}%</span>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const StatMini = ({ icon: Icon, label, value, color, bg, secondaryValue, secondaryLabel, infoText }: any) => {
    const [showTooltip, setShowTooltip] = useState(false);
    return (
        <div className="relative flex items-center gap-2.5 bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/50 rounded-xl p-2.5 min-w-0 shadow-sm transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600/50">
            <div className={`p-2 rounded-lg ${bg} shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1.5 relative">
                    <p className="text-[10px] text-gray-600 dark:text-gray-300 font-black uppercase tracking-[0.15em] leading-none truncate">{label}</p>
                    {infoText && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowTooltip(!showTooltip);
                            }}
                            className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors cursor-pointer focus:outline-none shrink-0"
                            title="Show Information"
                        >
                            <Info size={11} className="text-gray-400" />
                        </button>
                    )}
                    {secondaryValue !== undefined && secondaryValue > 0 && (
                        <span className="flex h-1.5 w-1.5 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500"></span>
                        </span>
                    )}
                </div>
                <div className="flex justify-between items-center gap-2">
                    <p className="text-lg font-black text-gray-900 dark:text-white leading-none tracking-tight">{value !== undefined ? value : 0}</p>
                    {secondaryValue !== undefined && secondaryValue > 0 && (
                        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/10 border border-orange-500/20 shadow-sm">
                            <span className="text-[11px] font-black text-orange-600 dark:text-orange-400 leading-none">{secondaryValue}</span>
                            <span className="text-[7.5px] text-orange-600/80 dark:text-orange-400/80 font-black uppercase tracking-widest leading-none">{secondaryLabel}</span>
                        </div>
                    )}
                </div>
            </div>

            {infoText && showTooltip && (
                <>
                    <div 
                        className="fixed inset-0 z-30" 
                        onClick={() => setShowTooltip(false)} 
                    />
                    <div className="absolute top-[calc(100%+6px)] left-0 right-0 z-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2.5 rounded-lg shadow-xl animate-in fade-in slide-in-from-top-1 duration-150">
                        <p className="text-[10px] text-gray-700 dark:text-gray-300 leading-normal font-medium">
                            {infoText}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

// Custom label for the Pie chart to display percentages correctly
// const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent }: any) => {
//     if (percent === 0) return null;
//     const RADIAN = Math.PI / 180;
//     const radius = outerRadius * 1.15; // Place slightly outside
//     const x = cx + radius * Math.cos(-midAngle * RADIAN);
//     const y = cy + radius * Math.sin(-midAngle * RADIAN);

//     return (
//         <text x={x} y={y} fill="#64748b" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="9" fontWeight="bold">
//             {`${(percent * 100).toFixed(1)}%`}
//         </text>
//     );
// };

const CameraDashboard = () => {
    const currentLevel = useClientLevel();
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<DashboardData | null>(null);

    // Pagination
    const [uptimePage, setUptimePage] = useState(0);
    const [flappingPage, setFlappingPage] = useState(0);
    const [isUptimePanelOpen, setIsUptimePanelOpen] = useState(false);
    const [uptimePanelPage, setUptimePanelPage] = useState(0);

    const [isFlappingPanelOpen, setIsFlappingPanelOpen] = useState(false);
    const [flappingPanelPage, setFlappingPanelPage] = useState(0);

    const [isReliabilityPanelOpen, setIsReliabilityPanelOpen] = useState(false);
    const [reliabilityPanelPage, setReliabilityPanelPage] = useState(0);

    const [isAlertsPanelOpen, setIsAlertsPanelOpen] = useState(false);
    const [alertsPanelPage, setAlertsPanelPage] = useState(0);
    const [alertsPanelTab, setAlertsPanelTab] = useState<'open' | 'closed'>('open');
    const [activeAlertTab, setActiveAlertTab] = useState<'open' | 'closed'>('open');
    const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
    const [alertCardPage, setAlertCardPage] = useState(0);

    const [selectedTrendAssetId, setSelectedTrendAssetId] = useState<number | null>(null);
    const [isTrendLoading, setIsTrendLoading] = useState(false);

    useEffect(() => {
        setAlertCardPage(0);
    }, [activeAlertTab]);

    useEffect(() => {
        setAlertsPanelPage(0);
    }, [alertsPanelTab]);

    const CARD_ROWS = 6;
    const PANEL_ROWS = 10;

    const isGlobal = currentLevel?.name === "global";

    useEffect(() => {
        const trends = data?.trend_analysis?.camera_downtime_trend || data?.camera_trend_30d || [];
        if (trends.length > 0 && !selectedTrendAssetId) {
            setSelectedTrendAssetId(trends[0].asset_id);
        }
    }, [data, selectedTrendAssetId]);

    useEffect(() => {
        if (selectedTrendAssetId) {
            setIsTrendLoading(true);
            const timer = setTimeout(() => setIsTrendLoading(false), 500);
            return () => clearTimeout(timer);
        }
    }, [selectedTrendAssetId]);

    useEffect(() => {
        if (currentLevel) {
            fetchData();
        }
    }, [currentLevel]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const client_id = isGlobal ? null : currentLevel?.id;
            const params: any = {};
            if (client_id) params.client_id = client_id;

            const res = await baseapi.get("/dashboard/camera_unified_dashboard", { params });
            if (res?.data?.status) {
                setData(res.data.data);
            }
        } catch (e: any) {
            showError("Failed to fetch camera dashboard data");
        } finally {
            setLoading(false);
        }
    };

    // --- Graphs & Derived Data ---

    const reachabilityPieData = useMemo(() => {
        if (!data?.overview) return [];
        const o = data.overview;
        const online = o.online ?? 0;
        const offline = o.offline ?? o.offline_cameras ?? 0;
        const totalGraph = online + offline;

        return [
            {
                name: "Online",
                value: online,
                color: "#10b981",
                displayPercent: totalGraph ? ((online / totalGraph) * 100).toFixed(1) : "0"
            },
            {
                name: "Offline",
                value: offline,
                color: "#f43f5e",
                displayPercent: totalGraph ? ((offline / totalGraph) * 100).toFixed(1) : "0"
            },
        ].filter(d => d.value > 0);
    }, [data]);

    const totalCameras = data?.overview?.total ?? data?.overview?.total_cameras ?? 0;

    const healthTrendData = useMemo(() => {
        if (!data?.health_timeseries) return [];
        return (data.health_timeseries || []).map(h => ({
            time: new Date(h.hour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            online: h.cameras_online,
            offline: h.cameras_offline,
            total: h.total_cameras,
            percentage: h.online_percentage,
        }));
    }, [data]);

    // Overall 30D Incident & Stability Trend
    const dailyEventsTrendData = useMemo(() => {
        const events = data?.trend_analysis?.daily_events_30d || [];
        if (events.length === 0) return [];
        return events
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map(e => ({
                time: new Date(e.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
                events: e.offline_events,
                affected: e.cameras_affected,
                still: e.still_offline_count
            }));
    }, [data]);

    const selectedCameraTrendData = useMemo(() => {
        const trends = data?.trend_analysis?.camera_downtime_trend || data?.camera_trend_30d || [];
        const selected = trends.find(t => t.asset_id === selectedTrendAssetId) || trends[0];
        if (!selected) return [];
        return selected.trend.map(d => ({
            time: new Date(d.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
            uptime: d.uptime_pct,
            downtime: d.downtime_minutes
        }));
    }, [data, selectedTrendAssetId]);

    const trendCameras = useMemo(() => {
        return data?.trend_analysis?.camera_downtime_trend || data?.camera_trend_30d || [];
    }, [data]);

    const trendOptions = useMemo(() => {
        return trendCameras.map(c => ({
            label: c.camera_name,
            value: c.asset_id
        }));
    }, [trendCameras]);

    const alertCardTotalPages = useMemo(() => {
        const alerts = activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || []);
        return Math.ceil(alerts.length / CARD_ROWS);
    }, [data, activeAlertTab, CARD_ROWS]);

    const getVisiblePages = (current: number, total: number) => {
        if (total <= 3) return Array.from({ length: total }, (_, i) => i);
        let start = Math.max(0, current - 1);
        if (start + 3 > total) start = total - 3;
        return [start, start + 1, start + 2];
    };

    const Pagination = ({ page, setPage, totalPages }: { page: number; setPage: (p: number) => void; totalPages: number }) => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex items-center justify-end gap-1 pt-1.5 border-t border-gray-100 dark:border-gray-700 mt-1">
                <button disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronLeft className="w-3 h-3" />
                </button>
                {getVisiblePages(page, totalPages).map(pi => (
                    <button key={pi} onClick={() => setPage(pi)}
                        className={`w-5 h-5 text-[9px] font-bold rounded transition-colors ${pi === page ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'}`}>
                        {pi + 1}
                    </button>
                ))}
                <button disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    className="w-5 h-5 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 border border-gray-300 dark:border-gray-600 text-[10px] transition-colors">
                    <ChevronRight className="w-3 h-3" />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-full flex flex-col items-center justify-center text-gray-700 dark:text-gray-100 py-12">
                <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin mb-3" />
                <p className="text-[11px] font-bold uppercase tracking-widest animate-pulse">Loading Camera Intelligence...</p>
            </div>
        );
    }

    if (!data) return <div className="min-h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm py-12">No data available</div>;

    const stats = data.overview;
    const reliabilityCameras = data.reliability_dashboard?.cameras || [];
    const filteredFlapping = data.flapping || [];
    const reliabilityTotalPages = Math.ceil(reliabilityCameras.length / CARD_ROWS);
    const flappingTotalPages = Math.ceil(filteredFlapping.length / CARD_ROWS);
    const reliabilityPanelTotalPages = Math.ceil(reliabilityCameras.length / PANEL_ROWS);
    const flappingPanelTotalPages = Math.ceil(filteredFlapping.length / PANEL_ROWS);

    const uptimePanelTotalPages = Math.ceil((data?.uptime?.cameras || []).length / PANEL_ROWS);

    const formatTime = (utcString?: string) => {
        if (!utcString) return "—";
        return new Date(utcString + "Z").toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="min-h-full xl:h-full bg-slate-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans flex flex-col pt-1">
            {/* Fleet Status Overview */}
            <div className="flex-none px-2 pb-1 z-20">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                    <StatMini icon={Cctv} label="Total Cameras" value={stats.total ?? stats.total_cameras} color="text-orange-500 dark:text-orange-400" bg="bg-orange-500/10" />
                    <StatMini icon={Wifi} label="Online" value={stats.online ?? 0} color="text-teal-500 dark:text-teal-400" bg="bg-teal-500/10" secondaryValue={data?.alerts?.stats?.auth_failure_open} secondaryLabel="Auth Fail" />
                    <StatMini icon={WifiOff} label="Offline" value={stats.offline ?? stats.offline_cameras} color="text-rose-500 dark:text-rose-400" bg="bg-rose-500/10" />
                    <StatMini 
                        icon={Cpu} 
                        label="Not Configured" 
                        value={stats.not_configured ?? 0} 
                        color="text-purple-500 dark:text-purple-400" 
                        bg="bg-purple-500/10" 
                        infoText="Not Configured cameras are excluded from monitoring metrics until credentials are configured"
                    />
                    <StatMini icon={HeartPulse} label="Health Score" value={`${(stats.health_percentage ?? stats.online_percentage ?? 0).toFixed(1)}%`} color="text-blue-500 dark:text-blue-400" bg="bg-blue-500/10" />
                </div>
            </div>

            {/* Main Content Areas */}
            <div className="flex-1 flex flex-col px-2 py-1 gap-2">

                {/* ROW 1: Camera Health (30%) & 30D Stability Trend (70%) */}
                <div className="flex-none xl:flex-[0.9] min-h-0 flex flex-col xl:grid xl:grid-cols-10 gap-2">
                    {/* Camera Health Pie - Takes 3 columns out of 10 (30%) on desktop */}
                    <div className="w-full xl:col-span-3 min-h-[300px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm overflow-hidden">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1 shrink-0">
                            <div className="p-1 rounded bg-teal-500/10 text-teal-500 dark:text-teal-400"><Wifi className="w-3 h-3" /></div>
                            Camera Health
                        </h3>
                        <div className="h-[220px] xl:h-full xl:flex-1 w-full min-h-[180px] flex items-center gap-1.5 overflow-hidden">
                            <div className="flex-1 h-full min-w-0 min-h-0">
                                {reachabilityPieData.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                        <Activity className="w-8 h-8 text-gray-400 dark:text-gray-200" />
                                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No health data available</span>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                                            <Pie
                                                data={reachabilityPieData}
                                                innerRadius="58%"
                                                outerRadius="80%"
                                                paddingAngle={3}
                                                dataKey="value"
                                                cornerRadius={4}
                                            >
                                                {reachabilityPieData.map((e, i) => <Cell key={i} fill={e?.color} stroke="none" />)}
                                                <Label
                                                    content={({ viewBox: { cx, cy } }: any) => (
                                                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                                                            <tspan x={cx} dy="-0.12em" fill={isDark ? "#f8fafc" : "#0f172a"} fontSize="16" fontWeight="black">{totalCameras}</tspan>
                                                            <tspan x={cx} dy="1.4em" fill={isDark ? "#94a3b8" : "#64748b"} fontSize="8" fontWeight="bold" letterSpacing="0.1em">FLEET</tspan>
                                                        </text>
                                                    )}
                                                />
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                            {/* Side Legend */}
                            <div className="flex flex-col justify-center gap-2.5 shrink-0 pr-2 border-l border-gray-200 dark:border-gray-700/50 pl-2.5 py-1">
                                {reachabilityPieData.map((e, i) => (
                                    <div key={i} className="flex items-center gap-1.5 group cursor-default">
                                        <div className="relative shrink-0">
                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                                            <div className="absolute inset-0 rounded-full blur-[2px] opacity-40" style={{ backgroundColor: e.color }} />
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[9px] text-gray-700 dark:text-gray-100 font-black uppercase tracking-wider leading-none mb-0.5 truncate">{e.name}</span>
                                            <span className="text-[10px] text-gray-500 dark:text-gray-300 font-bold font-mono">{e.displayPercent}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 30D Overall Trend Area Chart - Takes 7 columns out of 10 (70%) on desktop */}
                    <div className="w-full xl:col-span-7 min-h-[320px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm overflow-hidden">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1 shrink-0">
                            <div className="p-1 rounded bg-indigo-500/10 text-indigo-500 dark:text-indigo-400"><TrendingUp className="w-3 h-3" /></div>
                            30D Stability Trend
                        </h3>
                        <div className="h-[250px] xl:h-full xl:flex-1 w-full min-h-[180px] min-w-0 overflow-hidden">
                            {dailyEventsTrendData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                    <TrendingUp className="w-8 h-8 text-gray-400 dark:text-gray-200" />
                                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No incident data available</span>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={dailyEventsTrendData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                        <defs>
                                             <linearGradient id="colorPulse" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorPersistence" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorAffected" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#818cf8" stopOpacity={0.1} />
                                                <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: isDark ? '#c8cdd3' : '#64748b', fontSize: 7, fontWeight: 'bold' }} interval={2} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: isDark ? '#ffffff' : '#475569', fontSize: 7, fontWeight: 'bold' }} domain={[0, 'dataMax + 1']} />
                                        <Tooltip content={<TrendTooltip />} />
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} vertical={false} />

                                        {/* Incident Pulse - Smooth Area */}
                                        <Area
                                            type="monotone"
                                            dataKey="events"
                                            stroke="#f43f5e"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorPulse)"
                                            dot={{ r: 2, fill: '#f43f5e', strokeWidth: 0 }}
                                            activeDot={{ r: 4, strokeWidth: 0 }}
                                            animationDuration={1000}
                                        />

                                        {/* Persistent Outage - Smooth Area */}
                                        <Area
                                            type="monotone"
                                            dataKey="still"
                                            stroke="#f59e0b"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorPersistence)"
                                            dot={{ r: 2, fill: '#f59e0b', strokeWidth: 0 }}
                                            activeDot={{ r: 4, strokeWidth: 0 }}
                                            animationDuration={1200}
                                        />

                                        {/* Camera Impact - Affected Units */}
                                        <Area
                                            type="monotone"
                                            dataKey="affected"
                                            stroke="#818cf8"
                                            strokeWidth={2}
                                            fillOpacity={1}
                                            fill="url(#colorAffected)"
                                            dot={{ r: 2, fill: '#818cf8', strokeWidth: 0 }}
                                            activeDot={{ r: 4, strokeWidth: 0 }}
                                            animationDuration={1400}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                        <div className="flex justify-center items-center gap-4 mt-1 border-t border-gray-100 dark:border-gray-700/30 pt-1 shrink-0">
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-sm" />
                                <span className="text-[7.5px] font-black text-gray-600 dark:text-gray-300 uppercase tracking-widest">Incidents</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm" />
                                <span className="text-[7.5px] font-black text-gray-600 dark:text-gray-300 uppercase tracking-widest">Persistence</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/50" />
                                <span className="text-[7.5px] font-black text-gray-600 dark:text-gray-300 uppercase tracking-widest">Camera Impact</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ROW 2: 30D Uptime & 24H Health Trends (50% each on desktop) */}
                <div className="flex-none xl:flex-[0.9] min-h-0 flex flex-col xl:grid xl:grid-cols-2 gap-2">

                    {/* Camera Downtime Trend with Dropdown */}
                    <div className="w-full min-h-[320px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between mb-1 shrink-0 gap-2">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 shrink-0">
                                <div className="p-1 rounded bg-blue-500/10 text-blue-500 dark:text-blue-400"><Cpu className="w-3 h-3" /></div>
                                Camera Performance
                            </h3>
                            <div className="flex items-center gap-1.5 overflow-hidden justify-end flex-1 min-w-0">
                                <span className="text-[8px] text-gray-600 dark:text-gray-300 font-black uppercase tracking-widest whitespace-nowrap shrink-0">Unit:</span>
                                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 dark:bg-gray-900/50 rounded border border-gray-200 dark:border-gray-700/40 min-w-0 max-w-[130px] sm:max-w-[180px]">
                                    <SingleSelectLocalSearchDropdown
                                        options={trendOptions}
                                        value={selectedTrendAssetId}
                                        onChange={setSelectedTrendAssetId}
                                        placeholder={selectedTrendAssetId ? "" : "Select Unit"}
                                        buttonClassName="!h-6 !py-0 !px-1 !bg-transparent !border-none !shadow-none !text-[10px] font-bold text-gray-800 dark:text-gray-100 uppercase truncate"
                                        className="w-full"
                                        searchInputPlaceholder="Filter units..."
                                    />
                                </div>
                                <button
                                    onClick={() => { setIsUptimePanelOpen(true); setUptimePanelPage(0); }}
                                    className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-bold uppercase tracking-wide transition-colors shrink-0 whitespace-nowrap"
                                >
                                    Show All →
                                </button>
                            </div>
                        </div>
                        <div className="h-[250px] xl:h-full xl:flex-1 w-full min-h-[180px] min-w-0 relative overflow-hidden">
                            {isTrendLoading && (
                                <div className="absolute inset-0 z-10 bg-white/60 dark:bg-gray-800/60 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2 rounded-lg transition-all animate-in fade-in duration-200">
                                    <div className="w-5 h-5 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                                    <span className="text-[8px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] animate-pulse">Syncing...</span>
                                </div>
                            )}
                            {selectedCameraTrendData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                    <Cpu className="w-8 h-8 text-gray-400 dark:text-gray-200" />
                                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No trend data</span>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={selectedCameraTrendData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorTrendSingle" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} vertical={false} />
                                        <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: isDark ? '#ffffff' : '#475569', fontSize: 7 }} interval={5} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: isDark ? '#fafafa' : '#475569', fontSize: 7 }} domain={[0, 100]} />
                                        <Tooltip content={({ active, payload }: any) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0]?.payload;
                                            return (
                                                <div className="bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-700 rounded-lg p-2 shadow-2xl backdrop-blur-md">
                                                    <p className="text-[9px] text-gray-900 dark:text-gray-200 font-black uppercase mb-1">{d.time}</p>
                                                    <div className="flex justify-between items-center gap-4">
                                                        <span className="text-[9px] text-blue-600 dark:text-blue-400 font-bold uppercase">Uptime</span>
                                                        <span className="text-[10px] text-gray-900 dark:text-white font-mono font-black">{d.uptime}%</span>
                                                    </div>
                                                    <div className="flex justify-between items-center gap-4 mt-1 border-t border-gray-100 dark:border-gray-700/50 pt-1">
                                                        <span className="text-[9px] text-rose-600 dark:text-rose-400 font-bold uppercase">Downtime</span>
                                                        <span className="text-[10px] text-gray-900 dark:text-white font-mono font-black">{d.downtime}m</span>
                                                    </div>
                                                </div>
                                            );
                                        }} />
                                        <Area type="monotone" dataKey="uptime" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorTrendSingle)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    {/* Health Trend Area Chart */}
                    <div className="w-full min-h-[320px] xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm overflow-hidden">
                        <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5 mb-1 shrink-0">
                            <div className="p-1 rounded bg-cyan-500/10 text-cyan-500 dark:text-cyan-400"><History className="w-3 h-3" /></div>
                            24H Health Trends
                        </h3>
                        <div className="h-[250px] xl:h-full xl:flex-1 w-full min-h-[180px] min-w-0 overflow-hidden">
                            {healthTrendData.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                    <History className="w-8 h-8 text-gray-400 dark:text-gray-200" />
                                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No historical data</span>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={healthTrendData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="colorHealth" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} vertical={false} />
                                        <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: isDark ? '#ffffff' : '#475569', fontSize: 7 }} interval={4} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: isDark ? '#ffffff' : '#475569', fontSize: 7 }} domain={[0, 100]} />
                                        <Tooltip content={<HealthTrendTooltip />} />
                                        <Area type="monotone" dataKey="percentage" name="Health %" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorHealth)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                </div>

                {/* ROW 3: Tables Grid */}
                <div className="flex-none xl:flex-[1.2] min-h-0 flex flex-col xl:grid xl:grid-cols-3 gap-2 pb-4 xl:pb-0">

                    {/* Reliability Dashboard Table */}
                    <div className="w-full h-[360px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 shrink-0">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-orange-500/10 text-orange-500 dark:text-orange-400"><Activity className="w-3 h-3" /></div>
                                Reliability Dashboard ({reliabilityCameras.length})
                            </h3>
                            <button
                                onClick={() => { setIsReliabilityPanelOpen(true); setReliabilityPanelPage(0); }}
                                className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-bold uppercase tracking-wide transition-colors"
                            >
                                Show All →
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-x-auto custom-scroll flex flex-col">
                            <div className="min-w-[320px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-4">Unit</div>
                                    <div className="col-span-2 text-center">Events</div>
                                    <div className="col-span-3 text-center">Downtime</div>
                                    <div className="col-span-3 text-right">Avg Recov</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {reliabilityCameras?.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                            <Activity className="w-6 h-6 text-gray-400 dark:text-gray-200" />
                                            <span className="text-[9px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No reliability data</span>
                                        </div>
                                    ) : (
                                        reliabilityCameras.slice(uptimePage * CARD_ROWS, (uptimePage + 1) * CARD_ROWS).map((cam, idx) => (
                                            <div key={`${cam.asset_id}-${idx}`} className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center">
                                                <div className="col-span-4 text-[9px] text-gray-900 dark:text-gray-200 font-semibold truncate" title={cam.camera_name}>{cam.camera_name || "—"}</div>
                                                <div className="col-span-2 text-center">
                                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${cam.total_offline_events > 5 ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'}`}>
                                                        {cam.total_offline_events}
                                                    </span>
                                                </div>
                                                <div className="col-span-3 text-center text-[9px] text-gray-700 dark:text-gray-200 font-mono">
                                                    {cam.total_downtime_minutes > 1440 ? `${(cam.total_downtime_minutes / 1440).toFixed(1)}d` : `${cam.total_downtime_minutes.toFixed(0)}m`}
                                                </div>
                                                <div className="col-span-3 text-right text-[9px] font-black font-mono text-blue-600 dark:text-blue-400">
                                                    {cam.avg_recovery_minutes?.toFixed(1)}m
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <Pagination page={uptimePage} setPage={setUptimePage} totalPages={reliabilityTotalPages} />
                            </div>
                        </div>
                    </div>

                    {/* Reliability/Flapping Table */}
                    <div className="w-full h-[360px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-1.5 shrink-0">
                            <h3 className="text-[10px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wide flex items-center gap-1.5">
                                <div className="p-1 rounded bg-amber-500/10 text-amber-500 dark:text-amber-400"><Activity className="w-3 h-3" /></div>
                                Flapping Analytics
                                <span className="text-[7px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest ml-1">(1Day)</span>
                            </h3>
                            <button
                                onClick={() => { setIsFlappingPanelOpen(true); setFlappingPanelPage(0); }}
                                className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-bold uppercase tracking-wide transition-colors"
                            >
                                Show All →
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-x-auto custom-scroll flex flex-col">
                            <div className="min-w-[320px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[8px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 shrink-0">
                                    <div className="col-span-4">Camera</div>
                                    <div className="col-span-2 text-center">Flaps</div>
                                    <div className="col-span-3 text-center">Last Flap</div>
                                    <div className="col-span-3 text-center">Total Down</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {filteredFlapping?.length === 0 ? (
                                        <div className="flex items-center justify-center h-full">
                                            <span className="text-gray-400 dark:text-gray-500 text-xs">No flapping data available</span>
                                        </div>
                                    ) : (
                                        filteredFlapping
                                            ?.slice(flappingPage * CARD_ROWS, (flappingPage + 1) * CARD_ROWS)
                                            .map((cam) => (
                                                <div
                                                    key={cam.asset_id}
                                                    className="grid grid-cols-12 gap-1 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700/40 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center"
                                                >
                                                    <div className="col-span-4 flex flex-col min-w-0">
                                                        <span className="text-[9px] text-gray-900 dark:text-gray-200 font-semibold truncate" title={cam.camera_name}>
                                                            {cam.camera_name ?? "—"}
                                                        </span>
                                                        <span className="text-[8px] text-gray-500 dark:text-gray-400 font-mono">
                                                            {cam.ip_address ?? "—"}
                                                        </span>
                                                    </div>

                                                    <div className="col-span-2 text-center">
                                                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${(cam.flap_count ?? 0) > 1 ? "bg-rose-500/10 text-rose-600 dark:text-rose-400" : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"}`}>
                                                            {cam.flap_count ?? 0}
                                                        </span>
                                                    </div>

                                                    <div className="col-span-3 text-center">
                                                        <span className="text-[8px] text-gray-600 dark:text-gray-300 font-mono">
                                                            {cam.last_flap ? new Date(cam.last_flap + (cam.last_flap.endsWith('Z') ? '' : 'Z')).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                                                        </span>
                                                    </div>

                                                    <div className="col-span-3 text-center text-[9px] text-gray-700 dark:text-gray-200 font-mono">
                                                        {cam.total_downtime_minutes?.toFixed(1) ?? 0}m
                                                    </div>
                                                </div>
                                            ))
                                    )}
                                </div>
                                <Pagination page={flappingPage} setPage={setFlappingPage} totalPages={flappingTotalPages} />
                            </div>
                        </div>
                    </div>

                    {/* Tabbed Alerts Section */}
                    <div className="w-full h-[360px] xl:h-auto xl:min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex flex-col shadow-sm">
                        <div className="flex items-center justify-between mb-2 shrink-0">
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setActiveAlertTab('open')}
                                    className={`text-[9px] font-bold uppercase transition-colors px-2 py-0.5 rounded-md ${activeAlertTab === 'open' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30' : 'text-gray-500 dark:text-gray-400 hover:text-rose-600 dark:hover:text-rose-300'}`}
                                >
                                    Open ({(data?.alerts?.open_alerts || []).length})
                                </button>
                                <button
                                    onClick={() => setActiveAlertTab('closed')}
                                    className={`text-[9px] font-bold uppercase transition-colors px-2 py-0.5 rounded-md ${activeAlertTab === 'closed' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30' : 'text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-300'}`}
                                >
                                    Resolved ({(data?.alerts?.closed_30d || []).length})
                                </button>
                            </div>
                            <button
                                onClick={() => { setIsAlertsPanelOpen(true); setAlertsPanelPage(0); }}
                                className="text-[8px] text-blue-600 dark:text-blue-400 font-bold uppercase hover:underline"
                            >
                                HISTORICAL →
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-x-auto custom-scroll flex flex-col">
                            <div className="min-w-[300px] flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-0.5 px-1 py-1 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-[7px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest shrink-0">
                                    <div className="col-span-5">Camera</div>
                                    <div className="col-span-4">Event</div>
                                    <div className="col-span-3 text-right">Time</div>
                                </div>
                                <div className="flex-1 overflow-y-auto custom-scroll">
                                    {(() => {
                                        const alerts = activeAlertTab === 'open' ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || []);
                                        if (alerts.length === 0) {
                                            return (
                                                <div className="flex flex-col items-center justify-center h-full gap-2 opacity-40">
                                                    <AlertCircle className="w-6 h-6 text-gray-400 dark:text-gray-200" />
                                                    <span className="text-[9px] font-bold text-gray-500 dark:text-gray-200 uppercase tracking-widest">No active incidents</span>
                                                </div>
                                            );
                                        }
                                        const paginated = (alerts.slice(alertCardPage * CARD_ROWS, (alertCardPage + 1) * CARD_ROWS));
                                        return paginated.map((alert, idx) => (
                                            <div
                                                key={alert?.id || idx}
                                                onClick={() => setSelectedAlert(alert)}
                                                className="grid grid-cols-12 gap-0.5 px-1 py-1 border-b border-gray-100 dark:border-gray-700/20 hover:bg-gray-50 dark:hover:bg-gray-700/30 items-center cursor-pointer transition-colors"
                                            >
                                                <div className="col-span-5 truncate">
                                                    <span className="text-[8.5px] text-gray-900 dark:text-gray-100 font-bold truncate block">
                                                        {alert?.camera_name || alert?.asset_hostname || alert?.ip_address || "Unknown"}
                                                    </span>
                                                </div>
                                                <div className="col-span-4">
                                                    <span className={`text-[6.5px] font-bold px-1 py-0.25 rounded uppercase tracking-tighter truncate block ${activeAlertTab === 'open' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                                        {alert?.event_type?.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                                <div className="col-span-3 text-right">
                                                    <span className="text-[8px] font-black text-gray-600 dark:text-gray-300 font-mono">
                                                        {formatTime(alert?.updated)}
                                                    </span>
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                                <Pagination page={alertCardPage} setPage={setAlertCardPage} totalPages={alertCardTotalPages} />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Uptime Full Analysis Side Panel */}
            <AnimatePresence>
                {isUptimePanelOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsUptimePanelOpen(false)}
                            className="fixed inset-0 bg-black/40 z-[100]"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[520px] max-w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-[101] flex flex-col shadow-2xl"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                        <HeartPulse className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-900 dark:text-gray-100 leading-tight">Camera Uptime Analytics</p>
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest">Complete 30D Performance Historical Data</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsUptimePanelOpen(false)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-[9px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-wider">
                                    <div className="col-span-1">#</div>
                                    <div className="col-span-4">Camera</div>
                                    <div className="col-span-3">IP Address</div>
                                    <div className="col-span-4 text-right">Uptime (30d)</div>
                                </div>
                                <div className="overflow-y-auto custom-scroll flex-1">
                                    {(() => {
                                        const sortedCameras = [...(data?.uptime?.cameras || [])]
                                            .sort((a, b) => (b.uptime_30d_percentage || 0) - (a.uptime_30d_percentage || 0));
                                        const paginated = sortedCameras.slice(uptimePanelPage * PANEL_ROWS, (uptimePanelPage + 1) * PANEL_ROWS);
                                        return paginated.map((cam, idx) => {
                                            const globalIdx = uptimePanelPage * PANEL_ROWS + idx;
                                            return (
                                                <div key={cam?.asset_id || idx} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors items-center">
                                                    <div className="col-span-1 text-[9px] text-gray-500 dark:text-gray-400 font-mono">{globalIdx + 1}</div>
                                                    <div className="col-span-4">
                                                        <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold truncate" title={cam?.camera_name || "—"}>{cam?.camera_name || "—"}</p>
                                                        <p className="text-[8px] text-gray-500 dark:text-gray-400 uppercase">{cam?.asset_status || "—"}</p>
                                                    </div>
                                                    <div className="col-span-3 text-[9px] text-gray-600 dark:text-gray-300 font-mono">{cam?.ip_address || "—"}</div>
                                                    <div className="col-span-4 text-right">
                                                        <div className="flex flex-col items-end">
                                                            <span className={`text-[10px] font-black font-mono ${(cam?.uptime_30d_percentage || 0) > 95 ? 'text-emerald-600 dark:text-emerald-400' : (cam?.uptime_30d_percentage || 0) > 80 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                                {cam?.uptime_30d_percentage || 0}%
                                                            </span>
                                                            <div className="w-20 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
                                                                <div
                                                                    className={`h-full ${(cam?.uptime_30d_percentage || 0) > 95 ? 'bg-emerald-500' : (cam?.uptime_30d_percentage || 0) > 80 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                                                    style={{ width: `${cam?.uptime_30d_percentage || 0}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                    <span className="text-[9px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-widest">
                                        Page {uptimePanelPage + 1} of {uptimePanelTotalPages || 1}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            disabled={uptimePanelPage === 0}
                                            onClick={() => setUptimePanelPage(p => Math.max(0, p - 1))}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                        >
                                            <ChevronLeft className="w-3 h-3" />
                                        </button>
                                        <button
                                            disabled={uptimePanelPage >= uptimePanelTotalPages - 1}
                                            onClick={() => setUptimePanelPage(p => Math.min(uptimePanelTotalPages - 1, p + 1))}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                        >
                                            <ChevronRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Reliability (Flapping) Full Analysis Side Panel */}
            <AnimatePresence>
                {isFlappingPanelOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsFlappingPanelOpen(false)}
                            className="fixed inset-0 bg-black/40 z-[100]"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[580px] max-w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-[101] flex flex-col shadow-2xl"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-amber-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-900 dark:text-gray-100 leading-tight">Flapping Intelligence Analytics</p>
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-black uppercase tracking-widest leading-none mt-0.5">(1Day Sampling Interval)</p>
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest">Identified Instability & Frequent Connectivity Drops</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsFlappingPanelOpen(false)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-[9px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-wider">
                                    <div className="col-span-4">Camera</div>
                                    <div className="col-span-2 text-center">Flaps</div>
                                    <div className="col-span-3 text-right">Total Downtime</div>
                                    <div className="col-span-3 text-right">Last Flap</div>
                                </div>

                                <div className="overflow-y-auto custom-scroll flex-1">
                                    {(() => {
                                        const flapping = data?.flapping || [];
                                        const paginated = flapping.slice(flappingPanelPage * PANEL_ROWS, (flappingPanelPage + 1) * PANEL_ROWS);
                                        return paginated.map((cam, idx) => (
                                            <div key={`${cam?.asset_id || idx}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors items-center">
                                                <div className="col-span-4 min-w-0">
                                                    <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold truncate" title={cam?.camera_name || "—"}>{cam?.camera_name || "—"}</p>
                                                    <p className="text-[8px] text-gray-500 dark:text-gray-400 font-mono truncate">{cam?.ip_address || "—"}</p>
                                                </div>
                                                <div className="col-span-2 text-center">
                                                    <span className={`inline-block text-[10px] font-black px-2 py-0.5 rounded ${(cam?.flap_count || 0) > 1 ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'}`}>
                                                        {cam?.flap_count || 0}
                                                    </span>
                                                </div>
                                                <div className="col-span-3 text-right text-[10px] text-gray-900 dark:text-gray-100 font-mono">
                                                    {cam?.total_downtime_minutes?.toFixed(1) || 0} min
                                                </div>
                                                <div className="col-span-3 text-right">
                                                    <p className="text-[9px] text-gray-600 dark:text-gray-300">
                                                        {cam?.last_flap
                                                            ? new Date(cam.last_flap + (cam.last_flap.endsWith('Z') ? '' : 'Z')).toLocaleDateString()
                                                            : "—"}
                                                    </p>
                                                    <p className="text-[8px] text-gray-400">
                                                        {cam?.last_flap
                                                            ? new Date(cam.last_flap + (cam.last_flap.endsWith('Z') ? '' : 'Z')).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                            : "—"}
                                                    </p>
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                    <span className="text-[9px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-widest">
                                        Page {flappingPanelPage + 1} of {flappingPanelTotalPages || 1}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            disabled={flappingPanelPage === 0}
                                            onClick={() => setFlappingPanelPage(p => Math.max(0, p - 1))}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                        >
                                            <ChevronLeft className="w-3 h-3" />
                                        </button>
                                        <button
                                            disabled={flappingPanelPage >= flappingPanelTotalPages - 1}
                                            onClick={() => setFlappingPanelPage(p => Math.min(flappingPanelTotalPages - 1, p + 1))}
                                            className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                        >
                                            <ChevronRight className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Critical Alerts Full Analysis Side Panel */}
            <AnimatePresence>
                {isAlertsPanelOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsAlertsPanelOpen(false)}
                            className="fixed inset-0 bg-black/40 z-[100]"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[620px] max-w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-[101] flex flex-col shadow-2xl"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-rose-500/5">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <div className="p-1.5 rounded bg-rose-500/10 text-rose-600 dark:text-rose-400">
                                            <AlertCircle className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <p className="text-[12px] font-bold text-gray-900 dark:text-gray-100 leading-tight">Historical Events</p>
                                            <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest">Connectivity & Authentication Audits</p>
                                        </div>
                                    </div>

                                    {/* Modal Tabs */}
                                    <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 pl-4 ml-2">
                                        <button
                                            onClick={() => setAlertsPanelTab('open')}
                                            className={`text-[9px] font-bold uppercase transition-colors px-3 py-1.5 rounded-md ${alertsPanelTab === 'open' ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border border-rose-500/30' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                                        >
                                            Open ({(data?.alerts?.open_alerts || []).length})
                                        </button>
                                        <button
                                            onClick={() => setAlertsPanelTab('closed')}
                                            className={`text-[9px] font-bold uppercase transition-colors px-3 py-1.5 rounded-md ${alertsPanelTab === 'closed' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
                                        >
                                            Resolved ({(data?.alerts?.closed_30d || []).length})
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsAlertsPanelOpen(false)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-[9px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-wider">
                                    <div className="col-span-4">Camera / Unit</div>
                                    <div className="col-span-5">Issue Details</div>
                                    <div className="col-span-3 text-right">Triggered At</div>
                                </div>

                                <div className="overflow-y-auto custom-scroll flex-1">
                                    {(() => {
                                        const filteredAlerts = alertsPanelTab === "open" ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || []);
                                        const sortedAlerts = [...filteredAlerts].sort((a, b) =>
                                            new Date(b.triggered_at || b.updated).getTime() - new Date(a.triggered_at || a.updated).getTime()
                                        );
                                        const paginated = sortedAlerts.slice(alertsPanelPage * PANEL_ROWS, (alertsPanelPage + 1) * PANEL_ROWS);

                                        if (sortedAlerts.length === 0) {
                                            return (
                                                <div className="flex flex-col items-center justify-center h-full gap-3 opacity-30">
                                                    <AlertCircle className="w-12 h-12 text-gray-400" />
                                                    <span className="text-[12px] font-bold uppercase tracking-widest font-mono">No Records Found</span>
                                                </div>
                                            );
                                        }

                                        return (
                                            <>
                                                {paginated.map((alert, idx) => (
                                                    <div
                                                        key={`${alert?.id || idx}`}
                                                        onClick={() => setSelectedAlert(alert)}
                                                        className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors items-start cursor-pointer"
                                                    >
                                                        <div className="col-span-4 min-w-0">
                                                            <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold truncate" title={alert?.camera_name || alert?.asset_hostname || alert?.ip_address || "Unknown Unit"}>{alert?.camera_name || alert?.asset_hostname || alert?.ip_address || "Unknown Unit"}</p>
                                                            <p className="text-[8px] text-gray-500 dark:text-gray-400 font-mono truncate">{alert?.ip_address || "No IP"}</p>
                                                            {alert?.asset_hostname && <p className="text-[8px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{alert.asset_hostname}</p>}
                                                        </div>
                                                        <div className="col-span-5">
                                                            <p className={`text-[9px] font-black uppercase mb-1 ${alert?.event_type?.includes("auth") ? "text-violet-600 dark:text-violet-400" : "text-rose-600 dark:text-rose-400"}`}>
                                                                {alert?.event_type?.replace(/_/g, " ") || "Security Event"}
                                                            </p>
                                                            <p className="text-[9px] text-gray-600 dark:text-gray-300 leading-relaxed italic border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                                                                {alert?.description || alert?.message || "No description available"}
                                                            </p>
                                                        </div>
                                                        <div className="col-span-3 text-right">
                                                            <p className="text-[9px] text-gray-600 dark:text-gray-300">
                                                                {alert?.triggered_at || alert?.updated
                                                                    ? new Date((alert.triggered_at || alert.updated) + "Z").toLocaleDateString()
                                                                    : "—"}
                                                            </p>
                                                            <p className="text-[8px] text-gray-400">
                                                                {alert?.triggered_at || alert?.updated
                                                                    ? new Date((alert.triggered_at || alert.updated) + "Z").toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                                    : "—"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                    {(() => {
                                        const filteredAlerts = alertsPanelTab === "open" ? (data?.alerts?.open_alerts || []) : (data?.alerts?.closed_30d || []);
                                        const totalPages = Math.ceil(filteredAlerts.length / PANEL_ROWS) || 1;
                                        return (
                                            <>
                                                <span className="text-[9px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-widest">
                                                    Page {alertsPanelPage + 1} of {totalPages}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        disabled={alertsPanelPage === 0}
                                                        onClick={() => setAlertsPanelPage(p => Math.max(0, p - 1))}
                                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                                    >
                                                        <ChevronLeft className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        disabled={alertsPanelPage >= totalPages - 1}
                                                        onClick={() => setAlertsPanelPage(p => Math.min(totalPages - 1, p + 1))}
                                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                                    >
                                                        <ChevronRight className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Security Alert Detail Deep-Dive Panel */}
            <AnimatePresence>
                {selectedAlert && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedAlert(null)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200]"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[540px] max-w-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 z-[201] flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)]"
                        >
                            <div className={`p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between ${selectedAlert.event_status === 'open' ? 'bg-rose-500/5' : 'bg-emerald-500/5'}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-xl ${selectedAlert.event_status === 'open' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>
                                        <AlertCircle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wider">{selectedAlert.event_type.replace(/_/g, ' ')}</h2>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest">{selectedAlert.event_status} Incident #{selectedAlert.id}</p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedAlert(null)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-4 space-y-6">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 rounded-xl p-3">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase mb-1">Target Camera</p>
                                        <p className="text-xs font-black text-gray-900 dark:text-white">{selectedAlert.camera_name || selectedAlert.asset_hostname || selectedAlert.ip_address || "Unknown Unit"}</p>
                                        <p className="text-[10px] text-gray-600 dark:text-gray-300 font-mono mt-1">{selectedAlert.ip_address || "No IP Registered"}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 rounded-xl p-3">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase mb-1">Incident Time</p>
                                        <p className="text-xs font-black text-gray-900 dark:text-white">{new Date(`${selectedAlert.updated}Z`).toLocaleString()}</p>
                                        <p className="text-[10px] text-gray-600 dark:text-gray-300 mt-1 italic">{selectedAlert.event_status === 'closed' ? 'Resolved' : 'Active'}</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 w-1 bg-blue-500 rounded-full" />
                                        <h3 className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest">Diagnostic Summary</h3>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                                        <p className="text-xs text-gray-900 dark:text-gray-100 font-bold leading-relaxed mb-3">{selectedAlert.message}</p>
                                        <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <p className="text-[10px] text-gray-600 dark:text-gray-300 leading-relaxed italic">{selectedAlert.description}</p>
                                        </div>
                                    </div>
                                </div>

                                {selectedAlert.event_data && (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <div className="h-4 w-1 bg-violet-500 rounded-full" />
                                            <h3 className="text-[10px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-widest">Event Intelligence</h3>
                                        </div>
                                        <div className="space-y-2">
                                            {(() => {
                                                try {
                                                    const payload = JSON.parse(selectedAlert.event_data!);
                                                    if (!Array.isArray(payload)) return null;
                                                    return payload.map((snap, i) => (
                                                        <div key={i} className="bg-gray-50 dark:bg-gray-950/50 border border-gray-200 dark:border-gray-800 rounded-lg p-3">
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="text-[9px] font-black font-mono text-violet-600 dark:text-violet-400">STATE: {snap.status?.toUpperCase()}</span>
                                                                <span className="text-[8px] text-gray-500 dark:text-gray-400 font-mono">{new Date(snap.created).toLocaleString()}</span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                                {Object.entries(snap.data || {}).map(([key, val]: [string, any]) => (
                                                                    <div key={key} className="flex flex-col">
                                                                        <span className="text-[7px] text-gray-500 dark:text-gray-400 uppercase font-black tracking-widest">{key.replace(/_/g, ' ')}</span>
                                                                        <span className="text-[10px] text-gray-700 dark:text-gray-300 font-mono truncate">
                                                                            {key === 'auth_failed_at' && val
                                                                                ? new Date(val).toLocaleString()
                                                                                : String(val)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ));
                                                } catch (e) {
                                                    return <pre className="text-[9px] bg-gray-50 dark:bg-gray-950 p-3 rounded text-gray-700 dark:text-gray-200 overflow-x-auto">{selectedAlert.event_data}</pre>;
                                                }
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/80">
                                <button onClick={() => setSelectedAlert(null)} className="w-full py-2.5 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-300 text-[10px] font-black uppercase tracking-widest rounded-xl border border-gray-300 dark:border-gray-700 transition-all">
                                    Dismiss Detailed View
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Reliability Dashboard Side Panel */}
            <AnimatePresence>
                {isReliabilityPanelOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsReliabilityPanelOpen(false)}
                            className="fixed inset-0 bg-black/40 z-[100]"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-full sm:w-[680px] max-w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-[101] flex flex-col shadow-2xl"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-orange-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-gray-900 dark:text-gray-100 leading-tight">Reliability Intelligence Dashboard</p>
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest">In-depth Recovery Metrics & Outage Analysis</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsReliabilityPanelOpen(false)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Reliability Stats Overview */}
                            {data?.reliability_dashboard?.stats && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                                    <div className="bg-white dark:bg-gray-800/80 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase mb-1">Fleet Avg Recovery</p>
                                        <p className="text-xl font-black text-blue-600 dark:text-blue-400 font-mono">{data.reliability_dashboard.stats.fleet_avg_recovery_minutes.toFixed(1)}m</p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800/80 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase mb-1">Never Recovered</p>
                                        <p className="text-xl font-black text-rose-600 dark:text-rose-500 font-mono">{data.reliability_dashboard.stats.cameras_never_recovered}</p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800/80 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                        <p className="text-[9px] text-gray-500 dark:text-gray-400 font-bold uppercase mb-1">Tracked Fleet</p>
                                        <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono">{data.reliability_dashboard.stats.total_cameras_tracked}</p>
                                    </div>
                                </div>
                            )}

                            <div className="flex-1 min-h-0 flex flex-col">
                                <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-[9px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-wider">
                                    <div className="col-span-4">Identity / Location</div>
                                    <div className="col-span-2 text-center">Incidents</div>
                                    <div className="col-span-2 text-center">Avg Recovery</div>
                                    <div className="col-span-2 text-center">Total Downtime</div>
                                    <div className="col-span-2 text-right">Status</div>
                                </div>
                                <div className="overflow-y-auto custom-scroll flex-1">
                                    {(() => {
                                        const paginated = reliabilityCameras.slice(reliabilityPanelPage * PANEL_ROWS, (reliabilityPanelPage + 1) * PANEL_ROWS);
                                        return paginated.map((cam, idx) => (
                                            <div
                                                key={`${cam.asset_id}-${idx}`}
                                                className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors items-center"
                                            >
                                                <div className="col-span-4 min-w-0">
                                                    <p className="text-[10px] text-gray-900 dark:text-gray-100 font-bold truncate">{cam.camera_name || "Unknown Unit"}</p>
                                                    <p className="text-[8px] text-gray-500 dark:text-gray-400 font-mono italic truncate">{cam.ip_address} • {cam.location || "-"}</p>
                                                </div>
                                                <div className="col-span-2 text-center">
                                                    <span className={`text-[10px] font-black px-2 py-0.5 rounded ${cam.total_offline_events > 5 ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                                                        {cam.total_offline_events}
                                                    </span>
                                                </div>
                                                <div className="col-span-2 text-center text-[10px] font-bold font-mono text-blue-600 dark:text-blue-400">
                                                    {cam.avg_recovery_minutes.toFixed(1)}m
                                                </div>
                                                <div className="col-span-2 text-center text-[10px] text-gray-700 dark:text-gray-300 font-mono">
                                                    {cam.total_downtime_hours > 24 ? `${(cam.total_downtime_hours / 24).toFixed(1)}d` : `${cam.total_downtime_hours.toFixed(1)}h`}
                                                </div>
                                                <div className="col-span-2 text-right">
                                                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${cam.asset_status === 'active' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'}`}>
                                                        {cam.asset_status}
                                                    </span>
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>

                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                <span className="text-[9px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-widest">
                                    Page {reliabilityPanelPage + 1} of {reliabilityPanelTotalPages || 1}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        disabled={reliabilityPanelPage === 0}
                                        onClick={() => setReliabilityPanelPage(p => Math.max(0, p - 1))}
                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                    >
                                        <ChevronLeft className="w-3 h-3" />
                                    </button>
                                    <button
                                        disabled={reliabilityPanelPage >= reliabilityPanelTotalPages - 1}
                                        onClick={() => setReliabilityPanelPage(p => Math.min(reliabilityPanelTotalPages - 1, p + 1))}
                                        className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30"
                                    >
                                        <ChevronRight className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CameraDashboard;